import { FileText, List, LayoutGrid, Plus, Pencil, Trash2, Upload, Loader2, History } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";
import { Switch } from "@/shared/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/shared/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { useFactoryStore } from "@/core/hooks/useFactory";
import { useAppScope } from "@/core/AppScopeContext";
import { useHasPermission } from "@/core/hooks/usePermissions";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  createChecklist,
  createChecklistItemsBatch,
  deleteChecklist,
  fetchChecklistsByFactoryId,
} from "@/modules/checklist/api";
import { Badge } from "@/shared/ui/badge";
import { Skeleton } from "@/shared/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import { format } from "date-fns";
import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { OperatorView } from "@/modules/checklist/components/OperatorView";
import { toast } from "sonner";

// Instruction type — now startup-only (other types removed)
export type InstructionType = 'startup';

const STARTUP_CONFIG = {
  label: 'Startup',
  color: "var(--primary)",
  description: 'Shift startup and pre-production work instructions',
};

interface FactoryArea {
  id: string;
  name: string;
  color: string | null;
}

interface ProductionLine {
  id: string;
  name: string;
  area_id: string;
}

interface Station {
  id: string;
  name: string;
  line_id: string;
}

interface Competency {
  id: string;
  name: string;
  description: string | null;
  color: string;
  is_global: boolean;
}

interface WorkInstruction {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  type: InstructionType;
  area_id: string | null;
  line_id: string | null;
  station_id: string | null;
  area: { id: string; name: string } | null;
  line: { id: string; name: string } | null;
  station: { id: string; name: string } | null;
  item_count: number;
  creator: { full_name: string | null; email: string } | null;
  competencies: Competency[];
}

export default function WorkInstructions() {
  const { selectedFactoryId } = useFactoryStore();
  const { effectiveMode, selectedTenantId } = useAppScope();
  const canEdit = useHasPermission('startup.edit');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importedFileName, setImportedFileName] = useState<string>("");
  
  // Create form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [assignment, setAssignment] = useState<string>("none");
  const [isActive, setIsActive] = useState(true);

  // Fetch factory structure for assignment dropdown
  const { data: factoryAreas = [] } = useQuery({
    queryKey: ["factory-areas", selectedTenantId, selectedFactoryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('core')
        .from("factory_areas")
        .select("id, name, color")
        .eq("tenant_id", selectedTenantId!)
        .eq("factory_id", selectedFactoryId!)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as FactoryArea[];
    },
    enabled: !!selectedTenantId && !!selectedFactoryId,
  });

  const { data: productionLines = [] } = useQuery({
    queryKey: ["production-lines", selectedTenantId, selectedFactoryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('core')
        .from("production_lines")
        .select("id, name, area_id")
        .eq("tenant_id", selectedTenantId!)
        .eq("factory_id", selectedFactoryId!)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as ProductionLine[];
    },
    enabled: !!selectedTenantId && !!selectedFactoryId,
  });

  const { data: stations = [] } = useQuery({
    queryKey: ["stations", selectedTenantId, selectedFactoryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('core')
        .from("stations")
        .select("id, name, line_id")
        .eq("tenant_id", selectedTenantId!)
        .eq("factory_id", selectedFactoryId!)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as Station[];
    },
    enabled: !!selectedTenantId && !!selectedFactoryId,
  });

  const { data: instructions, isLoading } = useQuery({
    queryKey: ['work-instructions', selectedTenantId, selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId || !selectedTenantId) return [];

      const data = await fetchChecklistsByFactoryId(selectedTenantId, selectedFactoryId);

      const creatorIds = [...new Set(data.map((c) => c.created_by).filter(Boolean))] as string[];
      let creatorsMap: Record<string, { full_name: string | null; email: string }> = {};
      
      if (creatorIds.length > 0) {
        const { data: profiles } = await supabase
          .schema('core')
          .from('profiles')
          .select('id, full_name, email')
          .in('id', creatorIds);
        
        if (profiles) {
          creatorsMap = Object.fromEntries(profiles.map(p => [p.id, { full_name: p.full_name, email: p.email }]));
        }
      }
      
      return data.map(item => ({
        id: item.id,
        name: item.name,
        description: item.description,
        is_active: item.is_active,
        created_at: item.created_at,
        updated_at: item.updated_at,
        created_by: item.created_by,
        type: 'startup' as InstructionType,
        area_id: item.area_id,
        line_id: item.line_id,
        station_id: item.station_id,
        area: item.area,
        line: item.line,
        station: item.station,
        item_count: item.checklist_items?.length || 0,
        creator: item.created_by ? creatorsMap[item.created_by] || null : null,
        competencies: (item.instruction_competencies || [])
          .map((wic) => wic.competency)
          .filter(Boolean) as Competency[],
      })) as WorkInstruction[];
    },
    enabled: !!selectedTenantId && !!selectedFactoryId,
  });

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; description: string; area_id: string | null; line_id: string | null; station_id: string | null; is_active: boolean }) => {
      if (!selectedTenantId) {
        throw new Error("No tenant selected");
      }
      if (!selectedFactoryId) {
        throw new Error("No factory selected");
      }

      return createChecklist({
        tenant_id: selectedTenantId,
        factory_id: selectedFactoryId,
        name: data.name,
        description: data.description || null,
        area_id: data.area_id,
        line_id: data.line_id,
        station_id: data.station_id,
        is_active: data.is_active,
        checklist_kind: "startup",
      });
    },
    onSuccess: (newInstruction) => {
      queryClient.invalidateQueries({ queryKey: ["work-instructions", selectedTenantId, selectedFactoryId] });
      toast.success("Work instruction created");
      resetCreateForm();
      navigate(`/instructions/${newInstruction.id}`);
    },
    onError: (error) => toast.error("Failed to create: " + error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!selectedTenantId) {
        throw new Error("No tenant selected");
      }
      await deleteChecklist(selectedTenantId, id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["work-instructions", selectedTenantId, selectedFactoryId] });
      toast.success("Work instruction deleted");
    },
    onError: (error) => toast.error("Failed to delete: " + error.message),
  });

  const resetCreateForm = () => {
    setName("");
    setDescription("");
    setAssignment("none");
    setIsActive(true);
    setIsCreateDialogOpen(false);
  };

  const resetImportForm = () => {
    setImportedFileName("");
    setIsImporting(false);
    setIsImportDialogOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a PDF or image file (PNG, JPG, WebP)");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setImportedFileName(file.name);
    setIsImporting(true);

    try {
      if (!selectedTenantId) {
        throw new Error("No tenant selected");
      }

      // Convert file to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64Data = result.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Call edge function to parse the document
      const { data: parseResult, error: parseError } = await supabase.functions.invoke('parse-work-instruction', {
        body: {
          documentBase64: base64,
          mimeType: file.type,
          documentName: file.name.replace(/\.[^/.]+$/, ""),
        },
      });

      if (parseError) throw parseError;
      if (!parseResult?.success) throw new Error(parseResult?.error || 'Failed to parse document');

      const parsedData = parseResult.data;

      // Create the checklist
      if (!selectedFactoryId) {
        throw new Error("No factory selected");
      }

      const newInstruction = await createChecklist({
        tenant_id: selectedTenantId,
        factory_id: selectedFactoryId,
        name: parsedData.name || file.name.replace(/\.[^/.]+$/, ""),
        description: parsedData.description || null,
        is_active: false,
        checklist_kind: 'startup',
      });

      // Create checklist items from parsed data
      if (parsedData.items && parsedData.items.length > 0) {
        const itemsToInsert = parsedData.items.map((item: {
          label: string;
          description?: string | null;
          item_type?:
            | 'yes_no'
            | 'text'
            | 'number'
            | 'photo'
            | 'dropdown'
            | 'field_lookup';
          is_required?: boolean;
          is_inspection?: boolean;
          dropdown_options?: string[];
        }, index: number) => ({
          checklist_id: newInstruction.id,
          tenant_id: selectedTenantId,
          label: item.label,
          description: item.description || null,
          item_type: item.item_type || 'yes_no',
          is_required: item.is_required ?? true,
          is_inspection: item.is_inspection ?? false,
          dropdown_options: item.dropdown_options ?? null,
          sort_order: index,
        }));

        try {
          await createChecklistItemsBatch(itemsToInsert);
        } catch (itemsError) {
          console.error('Error creating items:', itemsError);
          toast.warning("Work instruction created but some items may not have been imported");
        }
      }

      queryClient.invalidateQueries({ queryKey: ["work-instructions", selectedTenantId, selectedFactoryId] });
      toast.success(`Imported "${parsedData.name}" with ${parsedData.items?.length || 0} items`);
      resetImportForm();
      
      navigate(`/instructions/${newInstruction.id}`);

    } catch (error) {
      console.error('Import error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to import document');
    } finally {
      setIsImporting(false);
    }
  };

  const parseAssignment = () => {
    if (assignment === "none") return { area_id: null, line_id: null, station_id: null };
    const [type, id] = assignment.split(":");
    return {
      area_id: type === "area" ? id : null,
      line_id: type === "line" ? id : null,
      station_id: type === "station" ? id : null,
    };
  };

  const handleCreate = () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }

    if (!selectedTenantId) {
      toast.error("No tenant selected");
      return;
    }

    const { area_id, line_id, station_id } = parseAssignment();
    createMutation.mutate({ name, description, area_id, line_id, station_id, is_active: isActive });
  };

  const handleDelete = (instruction: WorkInstruction) => {
    if (confirm(`Delete "${instruction.name}"? This will also delete all items in this work instruction.`)) {
      deleteMutation.mutate(instruction.id);
    }
  };

  const getScopeLabel = (instruction: WorkInstruction) => {
    if (instruction.station) return instruction.station.name;
    if (instruction.line) return instruction.line.name;
    if (instruction.area) return instruction.area.name;
    return 'Factory-wide';
  };

  const getScopeLevel = (instruction: WorkInstruction) => {
    if (instruction.station) return 'Station';
    if (instruction.line) return 'Line';
    if (instruction.area) return 'Area';
    return 'Factory';
  };

  const allInstructions = instructions || [];

  if (!selectedFactoryId) {
    return (
      <div className="space-y-4 md:space-y-6 animate-fade-in">
        <div className="bg-card rounded-xl shadow-card border border-border p-12 text-center">
          <h2 className="text-xl font-semibold text-foreground mb-2">No Factory Selected</h2>
          <p className="text-muted-foreground">Please select a factory to access Work Instructions.</p>
        </div>
      </div>
    );
  }

  // Operator mode
  if (effectiveMode === 'operator') {
    return (
      <div className="space-y-4 md:space-y-6 animate-fade-in">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground">Work Instructions</h1>
          <p className="text-sm text-muted-foreground mt-1 hidden sm:block">
            Complete startup work instructions for your shift
          </p>
        </div>

        <OperatorView factoryId={selectedFactoryId} tenantId={selectedTenantId} />
      </div>
    );
  }

  // Supervisor mode - overview
  return (
    <div className="space-y-4 md:space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground">Work Instructions</h1>
          <p className="text-sm text-muted-foreground mt-1 hidden sm:block">
            Manage startup work instructions for production operations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center border border-border rounded-lg p-1">
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-7 px-2"
              onClick={() => setViewMode('list')}
            >
              <List className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-7 px-2"
              onClick={() => setViewMode('grid')}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>
          {canEdit && (
            <>
              <Dialog open={isImportDialogOpen} onOpenChange={(open) => { if (!open) resetImportForm(); setIsImportDialogOpen(open); }}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Upload className="w-4 h-4" />
                    <span className="hidden sm:inline">Import PDF</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Import Work Instruction from PDF</DialogTitle>
                    <DialogDescription>
                      Upload a PDF or image document to automatically extract work instruction steps using AI.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div 
                      className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,image/png,image/jpeg,image/jpg,image/webp"
                        onChange={handleFileImport}
                        className="hidden"
                        disabled={isImporting}
                      />
                      {isImporting ? (
                        <div className="flex flex-col items-center gap-3">
                          <Loader2 className="w-10 h-10 text-primary animate-spin" />
                          <div>
                            <p className="font-medium text-foreground">Processing document...</p>
                            <p className="text-sm text-muted-foreground">{importedFileName}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-3">
                          <Upload className="w-10 h-10 text-muted-foreground" />
                          <div>
                            <p className="font-medium text-foreground">Click to upload or drag and drop</p>
                            <p className="text-sm text-muted-foreground">PDF, PNG, JPG up to 10MB</p>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p>The AI will analyze your document and:</p>
                      <ul className="list-disc list-inside space-y-0.5 ml-2">
                        <li>Extract work instruction name and description</li>
                        <li>Create instruction items from steps in the document</li>
                        <li>Determine appropriate item types (yes/no, text, number, etc.)</li>
                        <li>Mark inspection items and required fields</li>
                      </ul>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" onClick={resetImportForm} className="flex-1" disabled={isImporting}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog open={isCreateDialogOpen} onOpenChange={(open) => { if (!open) resetCreateForm(); setIsCreateDialogOpen(open); }}>
                <DialogTrigger asChild>
                  <Button variant="industrial" size="sm" className="gap-2">
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">New Work Instruction</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Create Startup Work Instruction</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g., Morning Shift Startup"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Optional description"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Assign To</Label>
                      <Select value={assignment} onValueChange={setAssignment}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          <SelectItem value="none">
                            <span className="font-medium">Factory-wide</span>
                          </SelectItem>
                          {factoryAreas.map((area) => (
                            <div key={area.id}>
                              <SelectItem value={`area:${area.id}`}>
                                <div className="flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: area.color || "var(--primary)" }} />
                                  <span className="font-medium">{area.name}</span>
                                </div>
                              </SelectItem>
                              {productionLines
                                .filter(line => line.area_id === area.id)
                                .map((line) => (
                                  <div key={line.id}>
                                    <SelectItem value={`line:${line.id}`}>
                                      <span className="pl-4 text-muted-foreground">&lsaquo; {line.name}</span>
                                    </SelectItem>
                                    {stations
                                      .filter(station => station.line_id === line.id)
                                      .map((station) => (
                                        <SelectItem key={station.id} value={`station:${station.id}`}>
                                          <span className="pl-8 text-muted-foreground">&lsaquo; {station.name}</span>
                                        </SelectItem>
                                      ))}
                                  </div>
                                ))}
                            </div>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="active">Active</Label>
                      <Switch id="active" checked={isActive} onCheckedChange={setIsActive} />
                    </div>
                    <div className="flex gap-2 pt-4">
                      <Button variant="outline" onClick={resetCreateForm} className="flex-1">
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleCreate} 
                        className="flex-1"
                        disabled={createMutation.isPending}
                      >
                        Create & Edit
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="bg-card rounded-xl border border-border p-4">
          <Skeleton className="h-10 w-full mb-2" />
          <Skeleton className="h-10 w-full mb-2" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : allInstructions.length > 0 ? (
        viewMode === 'list' ? (
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead className="hidden md:table-cell">Scope</TableHead>
                  <TableHead className="hidden lg:table-cell">Competencies</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead className="hidden sm:table-cell">Status</TableHead>
                  {canEdit && <TableHead className="w-[80px]">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {allInstructions.map((instruction) => (
                  <TableRow key={instruction.id} className={!instruction.is_active ? 'opacity-50' : ''}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${STARTUP_CONFIG.color}20` }}
                        >
                          <FileText className="w-4 h-4" style={{ color: STARTUP_CONFIG.color }} />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{instruction.name}</p>
                          {instruction.description && (
                            <p className="text-xs text-muted-foreground line-clamp-1 max-w-[200px]">
                              {instruction.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex flex-col">
                        <span className="text-xs text-muted-foreground">{getScopeLevel(instruction)}</span>
                        <span className="font-medium">{getScopeLabel(instruction)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {(instruction.competencies || []).slice(0, 3).map((comp) => comp && (
                          <span
                            key={comp.id}
                            className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-md bg-muted"
                          >
                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: comp.color || "var(--primary)" }} />
                            {comp.name}
                          </span>
                        ))}
                        {(instruction.competencies || []).length > 3 && (
                          <span className="text-xs text-muted-foreground">+{(instruction.competencies || []).length - 3}</span>
                        )}
                        {(instruction.competencies || []).length === 0 && (
                          <span className="text-xs text-muted-foreground">None</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(instruction.updated_at), 'MMM d, yyyy HH:mm')}
                      </span>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <Badge variant={instruction.is_active ? 'default' : 'secondary'}>
                        {instruction.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                     {canEdit && (
                       <TableCell>
                         <div className="flex items-center gap-1">
                           <Button 
                             variant="ghost" 
                             size="icon" 
                             className="h-8 w-8"
                             title="View completions"
                             onClick={() => navigate(`/instructions/${instruction.id}?tab=completions`)}
                           >
                             <History className="w-4 h-4" />
                           </Button>
                           <Button 
                             variant="ghost" 
                             size="icon" 
                             className="h-8 w-8"
                             onClick={() => navigate(`/instructions/${instruction.id}`)}
                           >
                             <Pencil className="w-4 h-4" />
                           </Button>
                           <Button 
                             variant="ghost" 
                             size="icon" 
                             className="h-8 w-8 text-destructive hover:text-destructive"
                             onClick={() => handleDelete(instruction)}
                           >
                             <Trash2 className="w-4 h-4" />
                           </Button>
                         </div>
                       </TableCell>
                     )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {allInstructions.map((instruction) => (
              <div
                key={instruction.id}
                className={`bg-card rounded-xl border border-border p-5 hover:border-primary/50 transition-colors ${!instruction.is_active ? 'opacity-50' : ''}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${STARTUP_CONFIG.color}20` }}
                  >
                    <FileText className="w-5 h-5" style={{ color: STARTUP_CONFIG.color }} />
                  </div>
                  <div className="flex items-center gap-1">
                     {canEdit && (
                       <>
                         <Button 
                           variant="ghost" 
                           size="icon" 
                           className="h-7 w-7"
                           title="View completions"
                           onClick={() => navigate(`/instructions/${instruction.id}?tab=completions`)}
                         >
                           <History className="w-3.5 h-3.5" />
                         </Button>
                         <Button 
                           variant="ghost" 
                           size="icon" 
                           className="h-7 w-7"
                           onClick={() => navigate(`/instructions/${instruction.id}`)}
                         >
                           <Pencil className="w-3.5 h-3.5" />
                         </Button>
                         <Button 
                           variant="ghost" 
                           size="icon" 
                           className="h-7 w-7 text-destructive hover:text-destructive"
                           onClick={() => handleDelete(instruction)}
                         >
                           <Trash2 className="w-3.5 h-3.5" />
                         </Button>
                       </>
                     )}
                   </div>
                </div>
                
                {!instruction.is_active && (
                  <div className="mb-2">
                    <Badge variant="secondary" className="text-xs">Inactive</Badge>
                  </div>
                )}
                
                <h3 className="font-semibold text-foreground mb-1">{instruction.name}</h3>
                {instruction.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                    {instruction.description}
                  </p>
                )}
                
                <div className="flex flex-wrap gap-1 mb-3">
                  {(instruction.competencies || []).slice(0, 4).map((comp) => comp && (
                    <span
                      key={comp.id}
                      className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-md bg-muted"
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: comp.color || "var(--primary)" }} />
                      {comp.name}
                    </span>
                  ))}
                  {(instruction.competencies || []).length > 4 && (
                    <span className="text-xs text-muted-foreground">+{(instruction.competencies || []).length - 4}</span>
                  )}
                </div>
                
                <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t border-border">
                  <span>{getScopeLevel(instruction)}: {getScopeLabel(instruction)}</span>
                  <span>{instruction.item_count} items</span>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="bg-card rounded-xl border border-border p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-muted-foreground" />
          </div>
          <h2 className="text-lg font-semibold text-foreground mb-2">No Work Instructions Yet</h2>
          <p className="text-muted-foreground mb-4 max-w-sm mx-auto">
            Create your first startup work instruction to get started with digital procedures
          </p>
          {canEdit && (
            <Button variant="industrial" onClick={() => setIsCreateDialogOpen(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              Create First Work Instruction
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
